import React, { useEffect, useRef, useState } from "react";
import RoundLoader from "./RoundLoader";
import "./FadeInMedia.css";

/**
 * Smooth image reveal — RoundLoader placeholder, then fade-in once loaded.
 * Handles cached images (onLoad can fire before the listener is attached).
 *
 * @param {boolean} [waiting=true] When src is empty: true = show loader, false = empty placeholder
 */
const FadeInMedia = ({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  style,
  objectFit = "cover",
  loaderSize = "sm",
  waiting = true,
  onLoad,
  onError,
  draggable = false,
  ...imgProps
}) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  // Cached / already-decoded images often skip onLoad — sync from the element.
  useEffect(() => {
    if (!src || failed) return undefined;
    const img = imgRef.current;
    if (!img) return undefined;

    const markLoaded = () => {
      if (img.naturalWidth > 0) {
        setLoaded(true);
      }
    };

    if (img.complete) {
      markLoaded();
    }

    const t = window.setTimeout(markLoaded, 0);
    return () => window.clearTimeout(t);
  }, [src, failed]);

  if (failed || (!src && !waiting)) {
    return (
      <div
        className={`fade-in-media fade-in-media--empty${className ? ` ${className}` : ""}`}
        style={style}
        aria-hidden
      />
    );
  }

  return (
    <div
      className={`fade-in-media${loaded ? " fade-in-media--ready" : ""}${
        className ? ` ${className}` : ""
      }`}
      style={style}
    >
      {!loaded && (
        <div className="fade-in-media__loader">
          <RoundLoader size={loaderSize} />
        </div>
      )}
      {src ? (
        <img
          {...imgProps}
          ref={imgRef}
          className={`fade-in-media__img${imgClassName ? ` ${imgClassName}` : ""}`}
          src={src}
          alt={alt}
          draggable={draggable}
          style={{ objectFit }}
          onLoad={(e) => {
            setLoaded(true);
            onLoad?.(e);
          }}
          onError={(e) => {
            setFailed(true);
            onError?.(e);
          }}
        />
      ) : null}
    </div>
  );
};

export default FadeInMedia;
